// ============================================================
// America's Energy Bridge — Internal Database Schemas
// These interfaces define the canonical shapes for the
// supplier network, buyer intake, and outreach tracking.
// CSV headers are exported for export/import workflows.
// ============================================================

export type VerificationStatus =
  | 'Pending Verification'
  | 'Verified'
  | 'Invite Only'
  | 'Rejected'

export type RelationshipStatus =
  | 'Not Contacted'
  | 'Researching'
  | 'Contacted'
  | 'Interested'
  | 'Approved'
  | 'Declined'

export interface Supplier {
  id: string
  company_name: string
  website: string
  state: string
  city: string
  category: string
  sub_category: string
  products_services: string
  certifications: string[]
  api_related: boolean
  iso_certified: boolean
  export_ready: boolean
  latin_america_experience: boolean
  spanish_support: boolean
  source_directory: string
  source_url: string
  contact_name: string
  contact_email: string
  contact_phone: string
  verification_status: VerificationStatus
  notes: string
  last_contacted: string
  relationship_status: RelationshipStatus
}

export interface BuyerRequest {
  id: string
  company_name: string
  rif: string
  contact_name: string
  title: string
  email: string
  whatsapp: string
  city: string
  state: string
  sector: string
  pdvsa_government_related:
    | 'Sí'
    | 'No'
    | 'No estoy seguro'
    | 'Prefiero explicarlo en una llamada'
    | ''
  requested_product: string
  category: string[]
  quantity: string
  budget: string
  urgency: string
  import_experience: string
  end_user: string
  files: string
  notes: string
  consent: boolean
  status: 'New' | 'Reviewing' | 'Matched' | 'Declined' | 'Closed'
}

export interface OutreachRecord {
  supplier_id: string
  company_name: string
  contact_person: string
  email: string
  phone: string
  source: string
  category: string
  outreach_date: string
  follow_up_date: string
  status:
    | 'Not Started'
    | 'Emailed'
    | 'Awaiting Reply'
    | 'Replied'
    | 'Meeting Set'
    | 'Closed'
  notes: string
  interested: boolean
  approved_for_network: boolean
}

// ============================================================
// CSV Headers
// ============================================================

export const SUPPLIER_CSV_HEADERS: (keyof Supplier)[] = [
  'id',
  'company_name',
  'website',
  'state',
  'city',
  'category',
  'sub_category',
  'products_services',
  'certifications',
  'api_related',
  'iso_certified',
  'export_ready',
  'latin_america_experience',
  'spanish_support',
  'source_directory',
  'source_url',
  'contact_name',
  'contact_email',
  'contact_phone',
  'verification_status',
  'notes',
  'last_contacted',
  'relationship_status',
]

export const BUYER_REQUEST_CSV_HEADERS: (keyof BuyerRequest)[] = [
  'id',
  'company_name',
  'rif',
  'contact_name',
  'title',
  'email',
  'whatsapp',
  'city',
  'state',
  'sector',
  'pdvsa_government_related',
  'requested_product',
  'category',
  'quantity',
  'budget',
  'urgency',
  'import_experience',
  'end_user',
  'files',
  'notes',
  'consent',
  'status',
]

export const OUTREACH_CSV_HEADERS: (keyof OutreachRecord)[] = [
  'supplier_id',
  'company_name',
  'contact_person',
  'email',
  'phone',
  'source',
  'category',
  'outreach_date',
  'follow_up_date',
  'status',
  'notes',
  'interested',
  'approved_for_network',
]
